import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getSystemCanvasControlTypes, getSystemCanvasControlTypeById } from '../../Pages/Library/CustomModule/Store/CustomModule.action';
import type { LibraryTitleInputProps } from './library-title-input.types';

export interface LibraryMatch {
  _id: string;
  title?: string;
}

interface ControlListResponse {
  data?: { controls?: LibraryMatch[] } | LibraryMatch[];
}

interface ControlDetailResponse {
  data?: { controlType?: LibraryControlRecord } | LibraryControlRecord;
}

export interface LibraryControlRecord {
  title?: string;
  content?: { type?: string; content?: unknown };
}

type AsyncDispatch = (action: unknown) => Promise<unknown>;

/**
 * Searches the Custom Module control library for one control type as the user types, and loads a
 * picked entry's saved configuration.
 * @param controlType - The control type to search within.
 * @param query - The text typed into the search box.
 * @returns The matches, whether a search is running or has finished, and a loader for a picked match.
 */
export default function useLibrarySearch(controlType: LibraryTitleInputProps['controlType'], query: string) {
  const dispatch = useDispatch() as unknown as AsyncDispatch;
  const [matches, setMatches] = useState<LibraryMatch[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    if (!trimmed) {
      setMatches([]);
      setIsSearching(false);
      setHasSearched(false);
      return undefined;
    }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = (await dispatch(getSystemCanvasControlTypes({ controltype: controlType, search: trimmed }))) as ControlListResponse | undefined;
        if (requestRef.current !== requestId) return;
        const data = response?.data;
        const list = Array.isArray(data) ? data : data?.controls ?? [];
        setMatches(list);
      } catch {
        if (requestRef.current === requestId) setMatches([]);
      } finally {
        if (requestRef.current === requestId) {
          setIsSearching(false);
          setHasSearched(true);
        }
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [dispatch, controlType, query]);

  const loadMatch = useCallback(
    async (match: LibraryMatch): Promise<LibraryControlRecord | null> => {
      try {
        const response = (await dispatch(getSystemCanvasControlTypeById(match._id))) as ControlDetailResponse | undefined;
        const data = response?.data;
        if (!data) return null;
        return 'controlType' in data && data.controlType ? data.controlType : (data as LibraryControlRecord);
      } catch {
        return null;
      }
    },
    [dispatch],
  );

  return { matches, isSearching, hasSearched, loadMatch };
}

const SEARCH_DEBOUNCE_MS = 500;
